import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, Store, Truck, Calendar } from "lucide-react";
import { api, inr } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = { Confirmed: "bg-blue-100 text-blue-700", "Ready for Pickup": "bg-purple-100 text-purple-700", Shipped: "bg-amber-100 text-amber-700", Delivered: "bg-green-100 text-green-700", Cancelled: "bg-red-100 text-red-700" };
const INSTALL_STEPS = ["Pending", "Scheduled", "Completed", "Confirmed"];

function InstallationPanel({ order, onChange }) {
  const [date, setDate] = useState(order.installation_date || "");
  const status = order.installation_status;
  const idx = INSTALL_STEPS.indexOf(status);

  const schedule = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    await api.post(`/orders/${order.id}/installation`, { action: "schedule", date });
    toast.success(status === "Scheduled" ? "Installation rescheduled" : "Installation scheduled");
    onChange();
  };
  const confirm = async () => {
    await api.post(`/orders/${order.id}/installation`, { action: "confirm" });
    toast.success("Installation confirmed — thank you!");
    onChange();
  };

  return (
    <div className="mt-3 rounded-2xl bg-secondary p-4" data-testid={`installation-panel-${order.order_number}`}>
      <p className="flex items-center gap-2 text-sm font-bold text-mart-wood"><Wrench className="h-4 w-4" /> Furniture Installation</p>
      <div className="mt-3 flex items-center gap-1">
        {INSTALL_STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center">
            <div className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold ${i <= idx ? "bg-mart-wood text-white" : "bg-white text-muted-foreground border border-border"}`}>{i + 1}</div>
            {i < INSTALL_STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < idx ? "bg-mart-wood" : "bg-border"}`} />}
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] font-semibold text-muted-foreground">
        {INSTALL_STEPS.map((s) => <span key={s} className="flex-1 text-center">{s}</span>)}
      </div>

      {(status === "Pending" || status === "Scheduled") && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <input type="date" value={date} min={new Date().toISOString().split("T")[0]} onChange={(e) => setDate(e.target.value)} data-testid={`install-date-${order.order_number}`} className="bg-transparent text-sm outline-none" />
          </div>
          <button onClick={schedule} data-testid={`install-schedule-${order.order_number}`} className="rounded-full bg-mart-wood px-5 py-2 text-sm font-bold text-white">{status === "Scheduled" ? "Reschedule" : "Schedule Installation"}</button>
          {status === "Scheduled" && order.installation_date && <span className="text-xs text-muted-foreground">Scheduled for {new Date(order.installation_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>}
        </div>
      )}
      {status === "Completed" && (
        <button onClick={confirm} data-testid={`install-confirm-${order.order_number}`} className="mt-3 rounded-full bg-mart-home px-5 py-2 text-sm font-bold text-white">Confirm Installation Done</button>
      )}
      {status === "Confirmed" && <p className="mt-3 text-sm font-semibold text-mart-home">✓ Installation completed & confirmed</p>}
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try { await api.post(`/orders/${id}/cancel`); toast.success("Order cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot cancel"); }
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold text-midnight mb-6">My Orders</h1>
      {orders.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border py-20 text-center">
          <p className="font-bold text-midnight">No orders yet</p>
          <Link to="/shop" className="mt-4 inline-block rounded-full bg-midnight px-6 py-2.5 font-bold text-white">Shop Now</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="rounded-3xl border border-border p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                <div><p className="text-sm font-bold text-midnight">{o.order_number}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p></div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[o.status] || "bg-secondary"}`}>{o.status}</span>
              </div>
              <div className="mt-3 flex gap-2 overflow-x-auto hide-scrollbar">
                {o.items.map((it, i) => <img key={i} src={it.product.images[0]} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />)}
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                {o.fulfillment_type === "pickup" ? <><Store className="h-3.5 w-3.5" /> Store Pickup{o.pickup_store ? ` • ${o.pickup_store.name}` : ""}</> : <><Truck className="h-3.5 w-3.5" /> {o.delivery_method}</>}
              </p>
              {o.installation_status && <InstallationPanel order={o} onChange={load} />}
              <div className="mt-3 flex items-center justify-between">
                <span className="font-extrabold text-midnight">{inr(o.grand_total)}</span>
                {["Confirmed", "Shipped", "Ready for Pickup"].includes(o.status) && <button onClick={() => cancel(o.id)} data-testid={`cancel-${o.order_number}`} className="text-sm font-semibold text-red-600 hover:underline">Cancel Order</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
